import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useScopedApiFilters, useTableStore } from '@/store/table/table'
import {
  Box,
  Select,
  MenuItem,
  FormControl,
  Typography,
  Button,
  TextField,
  InputLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  LinearProgress,
} from '@mui/material'
import DataGridPremiumDemo from '@/components/Table/DataGrid'
import PageHeaderWithIcons from '@/components/heder/PageHeaderWithIcons'
import FileIcon from '@assets/ReportGeneration/File.svg'
import { getSelectedMeterReadings, exportSelectedMeterReadings } from '@/services/consumerService'
import { exportToPDF, copyToClipboard } from '@/utils/exportUtils'
import { useAppTheme } from '@/theme/ThemeContext'
import AppDatePicker from '@/components/DatePickers/AppDatePicker'
import AppDateRangePicker from '@/components/DatePickers/AppDateRangePicker'
import {
  formatDay,
  parseDay,
  type DateRangeValue,
} from '@/components/DatePickers/dateRange'
import DetailedReadingsReport, {
  type DetailedReadingsHandle,
} from './reports/DetailedReadingsReport'
import {
  DETAILED_READINGS_REPORT,
  opensDetailedReadings,
} from './reports/detailedReadingsForm'

const reportOptions = [
  // 'דוח מדי קר״מ',
  // 'צרכנים',
  // 'צריכה',
  // 'התראות',
  // 'קריאות מדים',
  // 'החלפות מדים',
  // 'התקנות מדי קר״מ',
  // 'קריאות לחיוב',
  // 'דוח תקשורת יומי',
  // 'דוח תקלות יומי',
  // 'מדים שלא בתקופת הגיול',
  'קריאות ממדים נבחרים',
  DETAILED_READINGS_REPORT,
]

import karamColumns from '@/components/features/config/columns/reportGeneration/karam.column.config'
import consumersColumns from '@/components/features/config/columns/reportGeneration/consumers.column.config'
import consumptionColumns from '@/components/features/config/columns/reportGeneration/consumption.column.config'
import alertsColumns from '@/components/features/config/columns/reportGeneration/alerts.column.config'
import meterReadingsColumns from '@/components/features/config/columns/reportGeneration/meterReadings.column.config'
import meterReplacementsColumns from '@/components/features/config/columns/reportGeneration/meterReplacements.columns'
import karamInstallationsColumns from '@/components/features/config/columns/reportGeneration/karamInstallations.column.config'
import billingReadingsColumns from '@/components/features/config/columns/reportGeneration/billingReadings.columns'
import dailyCommunicationColumns from '@/components/features/config/columns/reportGeneration/dailyCommunication.column.config'
import dailyErrorsColumns from '@/components/features/config/columns/reportGeneration/dailyErrors.column.config'
import outOfPeriodMetersColumns from '@/components/features/config/columns/reportGeneration/outOfPeriodMeters.column.config'
import {
  buildSelectedMeterReadingsColumns,
  flattenSelectedMeterReading,
} from '@/components/features/config/columns/reportGeneration/selectedMeterReadings.column.config'
import {
  getAllAlertTypeTwo,
  getAllConsumptions,
  getAllMeterChanges,
  getAllMeterCommunication,
  getAllMeterExpire,
  getAllMeterIsuse,
  getAllMeterPrepare,
  getAllRows,
} from '@/local_db/db'

const LABEL_TO_COLUMNS: Record<string, any> = {
  'דוח מדי קר״מ': karamColumns,
  צרכנים: consumersColumns,
  צריכה: consumptionColumns,
  התראות: alertsColumns,
  'קריאות מדים': meterReadingsColumns,
  'החלפות מדים': meterReplacementsColumns,
  'התקנות מדי קר״מ': karamInstallationsColumns,
  'קריאות לחיוב': billingReadingsColumns,
  'דוח תקשורת יומי': dailyCommunicationColumns,
  'דוח תקלות יומי': dailyErrorsColumns,
  'מדים שלא בתקופת הגיול': outOfPeriodMetersColumns,
  'קריאות ממדים נבחרים': null, // built dynamically from API response
}

const STATUS_OPTIONS = ['יומי', 'שבועי', 'חודשי']
const INTERVAL_OPTIONS = ['יומי', 'שבועי', 'חודשי', 'דו-חודשי']
const ZONES = ['1', '2', '3', '4', '5']
const YEARS = ['2024', '2025']
const PERIODS = ['01-12', '13-24', '25-36', '37-48', '49-60', '61-72']
const MONTHS = [
  'כל השנה',
  'ינואר',
  'פברואר',
  'מרץ',
  'אפריל',
  'מאי',
  'יוני',
  'יולי',
  'אוגוסט',
  'ספטמבר',
  'אוקטובר',
  'נובמבר',
  'דצמבר',
]

type FiltersState = {
  fromDate?: string
  toDate?: string
  date?: string
  status?: string
  interval?: string
  zone?: string
  year?: string
  period?: string
  month?: string
  selectedMeterInputType?: 'meterNumbers' | 'waterCards'
  selectedMeterInput?: string
}

const initialFilters: FiltersState = {
  fromDate: '',
  toDate: '',
  date: '',
  status: '',
  interval: '',
  zone: '',
  year: '',
  period: '',
  month: '',
  selectedMeterInputType: 'meterNumbers',
  selectedMeterInput: '',
}

const Wrap: React.FC<{
  children: React.ReactNode
  sx?: React.CSSProperties
}> = ({ children, sx }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ...sx }}>
    {children}
  </Box>
)

const ReportGeneration: React.FC = () => {
  const { langKey } = useAppTheme()
  const [refreshExportRows, setRefreshExportRows] = useState(0)
  const [searchParams] = useSearchParams()
  const [selected, setSelected] = useState<string>(() =>
    opensDetailedReadings(searchParams) ? DETAILED_READINGS_REPORT : ''
  )
  const detailedReadings = useRef<DetailedReadingsHandle>(null)
  const hasOwnSearch =
    selected === 'קריאות ממדים נבחרים' || selected === DETAILED_READINGS_REPORT
  const [filters, setFilters] = useState<FiltersState>(initialFilters)
  const [rangeError, setRangeError] = useState(false)
  const [rows, setRows] = useState<any[]>([])
  const [fakeRowsCount, setFakeRowsCount] = useState<number | null>(null)
  const [selectedMeterSearched, setSelectedMeterSearched] = useState(false)
  const [selectedMeterColumns, setSelectedMeterColumns] = useState<any[] | null>(null)
  const [loading, setLoading] = useState(false)
  const [selectedMeterTotal, setSelectedMeterTotal] = useState(0)
  const [selectedMeterPage, setSelectedMeterPage] = useState(0)
  const [selectedMeterPageSize, setSelectedMeterPageSize] = useState(50)
  const [selectedMeterSortBy, setSelectedMeterSortBy] = useState('billing_id')
  const [selectedMeterSortOrder, setSelectedMeterSortOrder] = useState<'ASC' | 'DESC'>('DESC')
  const apiFilters = useScopedApiFilters('report-generation')

  const handleChangeReport = (value: string) => {
    useTableStore.getState().clearApiFilters()
    setSelected(value)
    setFilters(initialFilters)
    setSelectedMeterSearched(false)
    setSelectedMeterColumns(null)
    setSelectedMeterTotal(0)
    setSelectedMeterPage(0)
    setRefreshExportRows(prev => prev + 1)
    switch (value) {
      case 'צרכנים':
      case 'דוח מדי קר״מ':
      case 'קריאות מדים':
        getAllRows().then(data => {
          setRows(data)
        })
        setFakeRowsCount(45749)
        break
      case 'צריכה':
      case 'קריאות לחיוב':
        getAllConsumptions().then((data: any) => {
          setRows(data)
        })
        setFakeRowsCount(45749)
        break
      case 'התראות':
        getAllAlertTypeTwo().then((data: any) => {
          setRows(data)
        })
        setFakeRowsCount(null)
        break
      case 'החלפות מדים':
        getAllMeterChanges().then((data: any) => {
          setRows(data)
        })
        setFakeRowsCount(null)
        break
      case 'התקנות מדי קר״מ':
        getAllMeterPrepare().then((data: any) => {
          setRows(data)
        })
        setFakeRowsCount(null)
        break
      case 'מדים שלא בתקופת הגיול':
        getAllMeterExpire().then((data: any) => {
          setRows(data)
        })
        setFakeRowsCount(null)
        break
      case 'דוח תקלות יומי':
        getAllMeterIsuse().then((data: any) => {
          setRows(data)
        })
        setFakeRowsCount(null)
        break
      case 'דוח תקשורת יומי':
        getAllMeterCommunication().then((data: any) => {
          setRows(data)
        })
        setFakeRowsCount(null)
        break
      case 'קריאות ממדים נבחרים':
        setRows([])
        setFakeRowsCount(null)
        break
    }
  }

  const columns = useMemo(() => {
    if (!selected) return null
    if (selected === 'קריאות ממדים נבחרים') {
      return selectedMeterSearched ? selectedMeterColumns : null
    }
    return LABEL_TO_COLUMNS[selected] ?? null
  }, [selected, selectedMeterSearched, selectedMeterColumns])

  const getSelectedMeterParams = useCallback(() => {
    const accountId = localStorage.getItem('accountId')
    if (!accountId || !filters.fromDate || !filters.toDate || !filters.selectedMeterInput?.trim()) return null

    const values = filters.selectedMeterInput
      .split(/[,\n]+/)
      .map(v => v.trim())
      .filter(Boolean)

    if (values.length === 0) return null

    return {
      accountId: Number(accountId),
      from: filters.fromDate,
      to: filters.toDate,
      filterType: (filters.selectedMeterInputType ?? 'meterNumbers') as 'meterNumbers' | 'waterCards',
      values,
      langKey,
    }
  }, [filters, langKey])

  const flattenRows = (data: any[]) => data.map(flattenSelectedMeterReading)

  const getExportColumns = useCallback(() => {
    if (!columns) return []
    return columns.map((c: any) => ({ headerName: c.headerName || '', field: c.field || '' }))
  }, [columns])

  const fetchAllForExport = useCallback(async () => {
    const baseParams = getSelectedMeterParams()
    if (!baseParams) return null

    const resp = await getSelectedMeterReadings({
      ...baseParams,
      page: 1,
      size: 999999,
    })

    return flattenRows(resp.result)
  }, [getSelectedMeterParams])

  const handleExcelExport = useCallback(async () => {
    const baseParams = getSelectedMeterParams()
    if (!baseParams) return
    setLoading(true)
    try {
      await exportSelectedMeterReadings(baseParams)
    } finally {
      setLoading(false)
    }
  }, [getSelectedMeterParams])

  const handlePdfExport = useCallback(async () => {
    const exportCols = getExportColumns()
    if (exportCols.length === 0) return
    setLoading(true)
    try {
      const allRows = await fetchAllForExport()
      if (allRows && allRows.length > 0) {
        exportToPDF(exportCols, allRows)
      }
    } finally {
      setLoading(false)
    }
  }, [getExportColumns, fetchAllForExport])

  const handleCopy = useCallback(() => {
    const exportCols = getExportColumns()
    if (exportCols.length === 0) return
    if (rows && rows.length > 0) {
      copyToClipboard(exportCols, rows)
    }
  }, [getExportColumns, rows])

  const handleSet = (key: keyof FiltersState, value: string) =>
    setFilters(prev => ({ ...prev, [key]: value }))

  const handleRange = (range: DateRangeValue) =>
    setFilters(prev => ({
      ...prev,
      fromDate: formatDay(range.from),
      toDate: formatDay(range.to),
    }))

  const renderFilters = () => {
    const DateRange = (
      <Wrap>
        <AppDateRangePicker
          value={{ from: parseDay(filters.fromDate), to: parseDay(filters.toDate) }}
          onChange={handleRange}
          onErrorChange={setRangeError}
          disableFuture
          clearable
        />
      </Wrap>
    )

    const SingleDate = (
      <Wrap>
        <AppDatePicker
          label="תאריך"
          value={parseDay(filters.date)}
          onChange={value => handleSet('date', formatDay(value))}
          disableFuture
          clearable
        />
      </Wrap>
    )

    const LabeledSelect: React.FC<{
      label: string
      value: string
      onChange: (v: string) => void
      options: string[]
      minWidth?: number
    }> = ({ label, value, onChange, options, minWidth = 150 }) => (
      <FormControl size="small" sx={{ minWidth }}>
        <InputLabel sx={{ direction: 'rtl' }}>{label}</InputLabel>
        <Select
          value={value}
          label={label}
          onChange={e => onChange(String(e.target.value))}
        >
          {options.map(opt => (
            <MenuItem key={opt} value={opt}>
              {opt}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    )

    switch (selected) {
      case 'דוח מדי קר״מ':
      case 'צרכנים':
        return null

      case 'צריכה':
      case 'התראות':
      case 'החלפות מדים':
      case 'התקנות מדי קר״מ':
      case 'דוח תקלות יומי':
        return DateRange

      case 'קריאות מדים':
        return (
          <Wrap>
            <LabeledSelect
              label="סטטוס"
              value={filters.status ?? ''}
              onChange={v => handleSet('status', v)}
              options={STATUS_OPTIONS}
              minWidth={150}
            />
            {SingleDate}
          </Wrap>
        )

      case 'קריאות לחיוב':
        return (
          <Wrap>
            <LabeledSelect
              label="שנה"
              value={filters.year ?? ''}
              onChange={v => handleSet('year', v)}
              options={YEARS}
              minWidth={110}
            />
            <LabeledSelect
              label="תקופה"
              value={filters.period ?? ''}
              onChange={v => handleSet('period', v)}
              options={PERIODS}
              minWidth={130}
            />
          </Wrap>
        )

      case 'דוח תקשורת יומי':
        return (
          <Wrap>
            <LabeledSelect
              label="אזור"
              value={filters.zone ?? ''}
              onChange={v => handleSet('zone', v)}
              options={ZONES}
              minWidth={120}
            />
            <LabeledSelect
              label="תדירות"
              value={filters.interval ?? ''}
              onChange={v => handleSet('interval', v)}
              options={INTERVAL_OPTIONS}
              minWidth={120}
            />
            {DateRange}
          </Wrap>
        )

      case 'מדים שלא בתקופת הגיול':
        return (
          <Wrap>
            <LabeledSelect
              label="שנה"
              value={filters.year ?? ''}
              onChange={v => handleSet('year', v)}
              options={YEARS}
              minWidth={110}
            />
            <LabeledSelect
              label="חודש"
              value={filters.month ?? ''}
              onChange={v => handleSet('month', v)}
              options={MONTHS}
              minWidth={150}
            />
          </Wrap>
        )

      case 'קריאות ממדים נבחרים':
        return (
          <Wrap sx={{ alignItems: 'flex-start', gap: 2 }}>
            <AppDateRangePicker
              fromLabel="ת.התחלה"
              toLabel="ת.סיום"
              value={{ from: parseDay(filters.fromDate), to: parseDay(filters.toDate) }}
              onChange={handleRange}
              onErrorChange={setRangeError}
              disableFuture
              clearable
            />
            <TextField
              multiline
              minRows={2}
              maxRows={4}
              placeholder={
                filters.selectedMeterInputType === 'waterCards'
                  ? 'הזן כרטיסי מים (מופרדים בפסיק או שורה חדשה)'
                  : 'הזן מספרי מדים (מופרדים בפסיק או שורה חדשה)'
              }
              value={filters.selectedMeterInput ?? ''}
              onChange={e => handleSet('selectedMeterInput', e.target.value)}
              sx={{
                direction: 'rtl',
                minWidth: 350,
                '& .MuiInputBase-root': { fontFamily: 'Rubik, sans-serif', fontSize: 14 },
              }}
            />
            <RadioGroup
              value={filters.selectedMeterInputType ?? 'meterNumbers'}
              onChange={e => handleSet('selectedMeterInputType', e.target.value)}
              sx={{ flexShrink: 0 }}
            >
              <FormControlLabel
                value="meterNumbers"
                control={<Radio size="small" />}
                label={<Typography sx={{ fontFamily: 'Rubik, sans-serif', fontSize: 14 }}>מספרי מדים</Typography>}
              />
              <FormControlLabel
                value="waterCards"
                control={<Radio size="small" />}
                label={<Typography sx={{ fontFamily: 'Rubik, sans-serif', fontSize: 14 }}>כרטיסי מים</Typography>}
              />
            </RadioGroup>
            <Button
              onClick={handleSearch}
              variant="outlined"
              disabled={loading || rangeError || !filters.fromDate || !filters.toDate || !filters.selectedMeterInput?.trim()}
              sx={(theme) => ({
                background: theme.palette.mode === 'dark' ? 'rgba(96, 165, 250, 0.15)' : '#E3F5FF',
                border: `1px solid ${theme.palette.divider}`,
                color: theme.palette.text.primary,
                fontWeight: 400,
                '&:hover': { background: theme.palette.mode === 'dark' ? 'rgba(96, 165, 250, 0.25)' : '#d6ecff' },
                alignSelf: 'center',
                flexShrink: 0,
                mr: 2,
              })}
            >
              חיפוש
            </Button>
          </Wrap>
        )

      default:
        return null
    }
  }

  const fetchSelectedMeterReadings = useCallback(async (page: number, size: number, sortBy: string, sortOrder: 'ASC' | 'DESC') => {
    const baseParams = getSelectedMeterParams()
    if (!baseParams) return

    setLoading(true)
    try {
      const resp = await getSelectedMeterReadings({
        ...baseParams,
        page: page + 1,
        size,
        sortBy,
        sortOrder,
        apiFilter: apiFilters.length > 0 ? { apiResources: apiFilters } : undefined,
      })

      setSelectedMeterColumns(buildSelectedMeterReadingsColumns(resp.result))
      setRows(flattenRows(resp.result))
      setSelectedMeterTotal(resp.total)
      setSelectedMeterSearched(true)
      setRefreshExportRows(prev => prev + 1)
    } finally {
      setLoading(false)
    }
  }, [getSelectedMeterParams, apiFilters])

  const filtersSeen = useRef(apiFilters)
  useEffect(() => {
    if (filtersSeen.current === apiFilters) return
    filtersSeen.current = apiFilters
    if (selected !== 'קריאות ממדים נבחרים' || !selectedMeterSearched) return
    setSelectedMeterPage(0)
    fetchSelectedMeterReadings(0, selectedMeterPageSize, selectedMeterSortBy, selectedMeterSortOrder)
  }, [apiFilters, selected, selectedMeterSearched, fetchSelectedMeterReadings, selectedMeterPageSize, selectedMeterSortBy, selectedMeterSortOrder])

  const handleSearch = async () => {
    if (selected === 'קריאות ממדים נבחרים') {
      setSelectedMeterPage(0)
      await fetchSelectedMeterReadings(0, selectedMeterPageSize, selectedMeterSortBy, selectedMeterSortOrder)
    }
  }

  const handleSelectedMeterPaginationChange = useCallback((newPage: number, newPageSize: number) => {
    setSelectedMeterPage(newPage)
    setSelectedMeterPageSize(newPageSize)
    fetchSelectedMeterReadings(newPage, newPageSize, selectedMeterSortBy, selectedMeterSortOrder)
  }, [fetchSelectedMeterReadings, selectedMeterSortBy, selectedMeterSortOrder])

  const handleSelectedMeterSortChange = useCallback((field: string, order: 'ASC' | 'DESC') => {
    setSelectedMeterSortBy(field)
    setSelectedMeterSortOrder(order)
    setSelectedMeterPage(0)
    fetchSelectedMeterReadings(0, selectedMeterPageSize, field, order)
  }, [fetchSelectedMeterReadings, selectedMeterPageSize])

  return (
    <Box
      width="100%"
      data-testid="report-generation-page"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
        height: '100%',
        overflowY: 'auto',
      }}
    >
      <PageHeaderWithIcons
        title="הפקת דוחות"
        onExcelExport={
          selected === DETAILED_READINGS_REPORT
            ? () => detailedReadings.current?.exportExcel()
            : handleExcelExport
        }
        onPdfExport={handlePdfExport}
        onCopy={handleCopy}
      />
      <Box sx={{ width: '100%', direction: 'rtl', px: 2 }}>
        <Box sx={{ display: 'flex', alignItems: selected === 'קריאות ממדים נבחרים' ? 'flex-start' : 'center', mb: 2, gap: 0.3 }}>
          <FormControl
            size="small"
            sx={{
              width: 220,
              direction: 'rtl',
              mt: 0,
              '& .MuiSelect-select': {},
            }}
          >
            <InputLabel id="report-type-label">סוג הדוח</InputLabel>
            <Select
              label="סוג הדוח"
              value={selected}
              labelId="report-type-label"
              onChange={e => handleChangeReport(String(e.target.value))}
            >
              <MenuItem value="">
                <em>בחירה</em>
              </MenuItem>
              {reportOptions.map((option, idx) => (
                <MenuItem key={idx} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {/* dynamic filters */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {renderFilters()}
          </Box>

          {!hasOwnSearch && (
            <Button
              onClick={handleSearch}
              variant="outlined"
              sx={(theme) => ({
                background: theme.palette.mode === 'dark' ? 'rgba(96, 165, 250, 0.15)' : '#E3F5FF',
                border: `1px solid ${theme.palette.divider}`,
                color: theme.palette.text.primary,
                '&:hover': { background: theme.palette.mode === 'dark' ? 'rgba(96, 165, 250, 0.25)' : '#d6ecff' },
                ml: 1,
              })}
              disabled={!selected || rangeError}
            >
              חיפוש
            </Button>
          )}
        </Box>

        {loading && <LinearProgress sx={{ width: '95%', borderRadius: 1 }} />}

        {columns ? (
          <Box sx={{ width: '95%', height: '75%' }}>
            {selected === 'קריאות ממדים נבחרים' ? (
              <DataGridPremiumDemo
                columns={columns}
                rows={rows}
                hideFooter
                headerPosition="bottom"
                width="100%"
                pageSize={selectedMeterPageSize}
                serverSideRowCount={selectedMeterTotal}
                serverSidePage={selectedMeterPage}
                onPaginationChange={handleSelectedMeterPaginationChange}
                paginationMode="server"
                sortingMode="server"
                filterMode="server"
                onSortChange={handleSelectedMeterSortChange}
                refreshExportRows={refreshExportRows}
              />
            ) : (
              <DataGridPremiumDemo
                fakeRowsCount={fakeRowsCount}
                columns={columns}
                rows={rows}
                hideFooter
                headerPosition="bottom"
                width="100%"
                pageSize={10}
                refreshExportRows={refreshExportRows}
              />
            )}
          </Box>
        ) : selected === DETAILED_READINGS_REPORT ? (
          <DetailedReadingsReport ref={detailedReadings} />
        ) : (
          <Box
            sx={{
              width: '100%',
              height: '50vh',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              flexDirection: 'column',
            }}
          >
            <img src={FileIcon} alt="FileIcon" />
            <Typography
              fontWeight={700}
              sx={{ my: 1, fontFamily: 'Rubik, sans-serif' }}
            >
              הפקת דוח
            </Typography>
            <Typography>
              {selected ? 'יש לבחור פרמטרים וללחוץ על חיפוש' : 'להצגת דוח יש לבחור סוג דוח'}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  )
}

export default ReportGeneration
